import React from "react";
import { MdSearch, MdClose } from "react-icons/md";

export type RemoteSelectOption = {
  value: number | string;
  label: string;
  description?: string;
};

export type RemoteSelectProps = {
  value: number | string | null;
  /** Render label for the currently selected value even if the option isn't in options. */
  valueLabel?: string;
  onChange: (next: number | string | null) => void;
  /** Async fetcher given a trimmed search term. */
  fetchOptions: (term: string) => Promise<RemoteSelectOption[]>;
  placeholder?: string;
  /** Placeholder shown in the open menu's search field. */
  searchPlaceholder?: string;
  debounceMs?: number;
  className?: string;
  disabled?: boolean;
};

export default function RemoteSelect({
  value,
  valueLabel,
  onChange,
  fetchOptions,
  placeholder,
  searchPlaceholder,
  debounceMs = 300,
  className = "",
  disabled,
}: RemoteSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [term, setTerm] = React.useState("");
  const [options, setOptions] = React.useState<RemoteSelectOption[]>([]);
  const [loading, setLoading] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const reqIdRef = React.useRef(0);

  React.useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  React.useEffect(() => {
    if (!open) return;
    const id = ++reqIdRef.current;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const results = await fetchOptions(term.trim());
        if (id === reqIdRef.current) setOptions(results);
      } finally {
        if (id === reqIdRef.current) setLoading(false);
      }
    }, debounceMs);
    return () => clearTimeout(t);
  }, [term, open, fetchOptions, debounceMs]);

  const label =
    valueLabel ??
    options.find((o) => String(o.value) === String(value))?.label ??
    (value !== null && value !== undefined ? String(value) : "");

  return (
    <div ref={containerRef} className={`relative ${className}`.trim()}>
      <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm shadow-sm dark:border-white/10 dark:bg-navy-900 dark:text-white">
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOpen((v) => !v)}
          className="flex-1 truncate text-start disabled:opacity-60"
        >
          {value ? (
            <span className="text-gray-800 dark:text-white">{label}</span>
          ) : (
            <span className="text-gray-400">{placeholder}</span>
          )}
        </button>
        {value ? (
          <button
            type="button"
            onClick={() => onChange(null)}
            className="text-gray-400 hover:text-gray-700 dark:hover:text-white"
            aria-label="Clear"
          >
            <MdClose className="h-4 w-4" />
          </button>
        ) : null}
      </div>
      {open && (
        <div className="absolute z-30 mt-1 w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl dark:border-white/10 dark:bg-navy-800">
          <div className="flex items-center gap-2 border-b border-gray-200 px-3 py-2 dark:border-white/10">
            <MdSearch className="h-4 w-4 text-gray-400" />
            <input
              autoFocus
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder={searchPlaceholder ?? placeholder}
              className="h-7 w-full border-0 bg-transparent text-sm outline-none dark:text-white"
            />
          </div>
          <div className="max-h-60 overflow-y-auto py-1">
            {loading ? (
              <div className="px-3 py-3 text-center text-xs text-gray-500">...</div>
            ) : options.length === 0 ? (
              <div className="px-3 py-3 text-center text-xs text-gray-500">—</div>
            ) : (
              options.map((opt) => (
                <button
                  key={String(opt.value)}
                  type="button"
                  onClick={() => {
                    onChange(opt.value);
                    setOpen(false);
                  }}
                  className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-start text-sm hover:bg-gray-100 dark:hover:bg-white/10 ${
                    String(value) === String(opt.value) ? "bg-brand-50 dark:bg-brand-500/10" : ""
                  }`}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium leading-6 text-gray-800 dark:text-white">{opt.label}</span>
                    {opt.description ? (
                      <span className="mt-0.5 block truncate text-xs leading-5 text-gray-500">{opt.description}</span>
                    ) : null}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
